import * as React from 'react';
import {weakMemo} from '@packages/utilities/memo';
import type {Box} from '@packages/utilities/box';
import {createSmartContext} from '@packages/utilities/smart-context';
import {noop} from 'lodash';
import type {AddLayer, ContextData, RemoveLayer, SurfaceData} from './types';

const {
  ContextProvider: OverlayContextProvider,
  useContext: useOverlayWithSelector,
} = createSmartContext<ContextData>([
  {
    box: {
      top: 0,
      left: 0,
      width: 0,
      height: 0,
    },
    addLayer: noop,
    removeLayer: noop,
    ref: null,
  },
]);

export {OverlayContextProvider};

const getFullOverlaySelector = (context: ContextData) => context;

export function useOverlay() {
  return useOverlayWithSelector(getFullOverlaySelector);
}

export const buildSurface = weakMemo((addLayer: AddLayer) =>
  weakMemo((removeLayer: RemoveLayer) =>
    weakMemo((box: Box) =>
      weakMemo((ref: any) => ({
        box,
        addLayer,
        removeLayer,
        ref,
      }))
    )
  )
);

export const makeOverlayContext = weakMemo(
  (surface: SurfaceData): ContextData => [surface]
);

export const addSurface = weakMemo((surface: SurfaceData) =>
  weakMemo((context: ContextData): ContextData => context.concat(surface))
);

export const getSurfaceBox = (context: ContextData): Box =>
  // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
  context[context.length - 1].box;

export const addLayer = (context: ContextData): AddLayer =>
  // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
  context[context.length - 1].addLayer;

export const removeLayer = (context: ContextData): RemoveLayer =>
  // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
  context[context.length - 1].removeLayer;

export const popSurface = weakMemo(
  (context: ContextData): ContextData =>
    context.length > 1 ? context.slice(0, -1) : context
);

/**
 * If you need to force a React subtree of `<Locator>`s to remeasure, wrap the
 * subtree with this context provider, and update the `value` prop to some new
 * value to cause a re-render.
 *
 * See also: https://github.com/webflow/webflow/issues/34043
 */
export const RefreshOverlay = React.memo<{
  value: unknown;
  children: React.ReactNode;
}>(
  ({
    // We don't actually need to reference `value` - it just triggers a refresh
    value: _,
    children,
  }) => {
    const overlayContext = useOverlay();

    return (
      <OverlayContextProvider value={[...overlayContext]}>
        {children}
      </OverlayContextProvider>
    );
  }
);
